import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
// @ts-ignore
import {useHistory} from 'react-router-dom';
import Switch from 'react-switch';
import {Col, FormFeedback, FormGroup, FormText, Input, Label, Row} from 'reactstrap';
import Button from 'src/components/button';
import useAlerts from 'src/hooks/use-alerts';
import {DelinquencySettings} from 'src/core/data/api/BillingBlock';
import {useDelinquencySettings} from './queries';
import {useUpdateDelinquencySettings} from './mutations';
import {useBillingBlock} from './provider';
import {QueryState, SubtabHeader} from './list-state';
import ConfirmDialog from './confirm-dialog';
import {errorMessageId} from './errors';
import styles from './billing-block.module.scss';

const DEFAULT_GRACE_DAYS = 5;

const asText = (graceDays: number | null) => (graceDays === null ? '' : String(graceDays));

// Empty means "use the server default"; anything else must be a whole number from 0 to 60.
export const parseGraceDays = (text: string): {valid: boolean; value: number | null} => {
  const trimmed = text.trim();
  if (trimmed === '') return {valid: true, value: null};
  if (!/^\d+$/.test(trimmed)) return {valid: false, value: null};
  const value = Number(trimmed);
  return {valid: value <= 60, value};
};

interface SettingsFormProps {
  saved: DelinquencySettings;
  readonly: boolean;
}

const SettingsForm = ({saved, readonly}: SettingsFormProps) => {
  const intl = useIntl();
  const {toast} = useAlerts();
  const {setDirty, confirmDiscard} = useBillingBlock();
  const history = useHistory();
  const mutation = useUpdateDelinquencySettings();
  const [graceText, setGraceText] = useState(asText(saved.graceDays));
  const [blockVehicles, setBlockVehicles] = useState(saved.blockVehicles);
  const [dialog, setDialog] = useState<'enable' | 'disable' | null>(null);

  useEffect(() => {
    setGraceText(asText(saved.graceDays));
    setBlockVehicles(saved.blockVehicles);
  }, [saved]);

  const dirty = graceText !== asText(saved.graceDays) || blockVehicles !== saved.blockVehicles;

  useEffect(() => {
    setDirty(dirty);
  }, [dirty, setDirty]);

  useEffect(() => () => setDirty(false), [setDirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // Returning false cancels the route change without the browser's confirm; the module's dialog asks instead.
  useEffect(() => {
    if (!dirty) return undefined;
    const unblock = history.block((location: {pathname: string; search: string; hash: string}) => {
      confirmDiscard(() => {
        unblock();
        history.push(location);
      });
      return false;
    });
    return unblock;
  }, [dirty, history, confirmDiscard]);

  const grace = parseGraceDays(graceText);
  const defaultDays = saved.graceDays === null ? saved.effectiveGraceDays : DEFAULT_GRACE_DAYS;
  const days = grace.value ?? defaultDays;

  const save = () => {
    mutation.mutate({graceDays: grace.value, blockVehicles}, {
      onSuccess: () => toast.success(intl.formatMessage({id: 'billingBlockSettingsSaved'})),
      onError: (error) => toast.error(intl.formatMessage({id: errorMessageId(error)})),
      onSettled: () => setDialog(null),
    });
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!grace.valid || mutation.isLoading) return;
    if (blockVehicles && !saved.blockVehicles) return setDialog('enable');
    if (!blockVehicles && saved.blockVehicles) return setDialog('disable');
    return save();
  };

  const helpId = 'billing-block-grace-help';
  const defaultId = 'billing-block-grace-default';
  const errorId = 'billing-block-grace-error';

  return (
    <form onSubmit={submit} noValidate>
      <Row form>
        <Col md={4}>
          <FormGroup className="custom-formGroup">
            <Label for="billing-block-grace">{intl.formatMessage({id: 'billingBlockGraceDays'})}</Label>
            <Input
              id="billing-block-grace"
              name="graceDays"
              inputMode="numeric"
              value={graceText}
              placeholder={String(defaultDays)}
              disabled={readonly}
              invalid={!grace.valid}
              aria-describedby={[helpId, graceText === '' ? defaultId : '', grace.valid ? '' : errorId].filter(Boolean).join(' ')}
              onChange={(event) => setGraceText(event.target.value)}
            />
            {!grace.valid && <FormFeedback id={errorId}>{intl.formatMessage({id: 'billingBlockGraceDaysInvalid'})}</FormFeedback>}
            {graceText === '' && (
              <FormText id={defaultId}>{intl.formatMessage({id: 'billingBlockGraceDaysDefault'}, {days: defaultDays})}</FormText>
            )}
          </FormGroup>
        </Col>
        <Col md={8}>
          <FormGroup className="custom-formGroup">
            <Label for="billing-block-devices">{intl.formatMessage({id: 'billingBlockBlockDevices'})}</Label>
            <div className={styles.switchRow}>
              <Switch
                id="billing-block-devices"
                checked={blockVehicles}
                disabled={readonly}
                onChange={setBlockVehicles}
                onColor="#86d3ff"
                onHandleColor="#2693e6"
                handleDiameter={30}
                uncheckedIcon={false}
                checkedIcon={false}
                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                height={20}
                width={48}
              />
            </div>
          </FormGroup>
        </Col>
      </Row>
      <p id={helpId} className={styles.hint}>{intl.formatMessage({id: 'billingBlockGraceDaysHelp'})}</p>
      {!readonly && (
        <div className="d-flex justify-content-end">
          <Button className="solid" color="theme" type="submit" disabled={!grace.valid || mutation.isLoading}>
            {intl.formatMessage({id: 'billingBlockSave'})}
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={dialog === 'enable'}
        title={intl.formatMessage({id: 'billingBlockEnableTitle'})}
        description={(
          <>
            <p>{intl.formatMessage({id: 'billingBlockEnableBody'}, {days})}</p>
            <p className={`${styles.warning} ${styles.term}`}>{intl.formatMessage({id: 'billingBlockResponsibilityTerm'})}</p>
          </>
        )}
        acceptLabel={intl.formatMessage({id: 'billingBlockEnableAccept'})}
        confirmLabel={intl.formatMessage({id: 'billingBlockEnableConfirm'})}
        busy={mutation.isLoading}
        onConfirm={save}
        onCancel={() => setDialog(null)}
      />
      <ConfirmDialog
        open={dialog === 'disable'}
        danger
        title={intl.formatMessage({id: 'billingBlockDisableTitle'})}
        description={<p>{intl.formatMessage({id: 'billingBlockDisableBody'})}</p>}
        confirmLabel={intl.formatMessage({id: 'billingBlockDisableConfirm'})}
        busy={mutation.isLoading}
        onConfirm={save}
        onCancel={() => setDialog(null)}
      />
    </form>
  );
};

interface SettingsTabProps {
  readonly: boolean;
}

const SettingsTab = ({readonly}: SettingsTabProps) => {
  const intl = useIntl();
  const query = useDelinquencySettings();
  return (
    <section className={styles.section}>
      <SubtabHeader title={intl.formatMessage({id: 'billingBlockSettingsTitle'})} query={query} />
      <QueryState query={query}>
        {() => query.data && <SettingsForm saved={query.data} readonly={readonly} />}
      </QueryState>
    </section>
  );
};

export default SettingsTab;
